import { ActiveStatuses } from '@/types/statuses.ts';
import { StepExecution } from '@/types/stepExecutions.ts';
import { formatDuration } from '@/utils/datetime.ts';

export type StepExecutionTimingState = 'finished' | 'running' | 'unavailable';

export type StepExecutionTiming = {
  state: StepExecutionTimingState;
  durationSeconds: number | null;
  durationDisplay: string;
};

type StepExecutionTimingInput = Pick<StepExecution, 'created_at' | 'completed_at' | 'status'>;

const parseDate = (value?: string | null): Date | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const secondsBetween = (start: Date, end: Date): number | null => {
  const seconds = (end.getTime() - start.getTime()) / 1000;
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : null;
};

const makeTiming = (
  state: StepExecutionTimingState,
  durationSeconds: number | null,
): StepExecutionTiming => ({
  state,
  durationSeconds,
  durationDisplay: formatDuration(durationSeconds),
});

export const getStepExecutionTiming = (
  stepExecution?: StepExecutionTimingInput | null,
  now: Date = new Date(),
): StepExecutionTiming => {
  if (!stepExecution) return makeTiming('unavailable', null);

  const startedAt = parseDate(stepExecution.created_at);
  if (!startedAt) return makeTiming('unavailable', null);

  const completedAt = parseDate(stepExecution.completed_at);
  if (completedAt) {
    const durationSeconds = secondsBetween(startedAt, completedAt);
    return durationSeconds === null
      ? makeTiming('unavailable', null)
      : makeTiming('finished', durationSeconds);
  }

  if (stepExecution.completed_at) {
    return makeTiming('unavailable', null);
  }

  if (!ActiveStatuses.includes(stepExecution.status)) {
    return makeTiming('unavailable', null);
  }

  const durationSeconds = secondsBetween(startedAt, now);
  return durationSeconds === null
    ? makeTiming('unavailable', null)
    : makeTiming('running', durationSeconds);
};
